import { MouseEvent, useState } from 'react';
import { NodeApi } from 'react-arborist';
import { classNames } from 'primereact/utils';
import {
  BundleTreeTypes,
  ChevronIcon,
  KebabMenuIcon,
  SectionsTreeNodeContent,
  TREE_INDENT_PX,
  TREE_ROW_HEIGHT,
} from '@repo/atomic-components';
import { BundleSectionsTreeNode } from '@/api';
import { StickyAncestorPin } from './useStickyAncestorPins';

interface BundleSectionsTreeStickyPinsProps {
  pins: StickyAncestorPin<BundleSectionsTreeNode>[];
  scrollbarWidth: number;
  overlayRef: React.RefObject<HTMLDivElement | null>;
  showKebab: boolean;
  // Same highlight props the real tree receives, so pinned rows can mirror
  // the "current viewed page's ancestor" colouring.
  manuallyHighlightedLightNodeIds?: string[] | null;
  manuallyHighlightedDarkNodeId?: string | null;
  onActivate: (node: NodeApi<BundleSectionsTreeNode>) => void;
  onOpenContextMenu: (e: MouseEvent<HTMLElement>, node: NodeApi<BundleSectionsTreeNode>) => void;
  // Called after the user collapses a pinned section via its chevron. Needed
  // because react-arborist's node.close() doesn't fire a scroll or resize
  // event, so the pin hook wouldn't otherwise know that pinned descendants
  // are now stale and need clearing.
  onAfterToggle?: () => void;
}

// Shallower pins sit on top so deeper pins slide out behind them cleanly.
const zIndexForType = (type: BundleSectionsTreeNode['type']): number => {
  if (type === BundleTreeTypes.PROVIDER) return 30;
  if (type === BundleTreeTypes.CATEGORY) return 29;
  return 28;
};

export const BundleSectionsTreeStickyPins: React.FC<BundleSectionsTreeStickyPinsProps> = ({
  pins,
  scrollbarWidth,
  overlayRef,
  showKebab,
  manuallyHighlightedLightNodeIds,
  manuallyHighlightedDarkNodeId,
  onActivate,
  onOpenContextMenu,
  onAfterToggle,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (!pins.length) return null;

  return (
    <div ref={overlayRef} className="absolute top-0 left-0 z-10" style={{ right: scrollbarWidth }}>
      {pins.map(({ node, level, top }) => {
        const isHovered = hoveredId === node.id;
        // Only the deepest node in the ancestor path lights up. Since pins
        // are always for open sections, they're the deepest visible ancestor
        // only when they're the last entry of the path (nothing below them
        // in the path is on screen because it hasn't been reached yet).
        const path = manuallyHighlightedLightNodeIds ?? [];
        const isLightGreen = path.length > 0 && path[path.length - 1] === node.id;
        const isDarkGreen = node.id === manuallyHighlightedDarkNodeId;
        // Kebab hover box: match its parent row's highlight so it doesn't
        // read as grey inside a green row.
        const buttonHover = isLightGreen || isDarkGreen ? 'hover:bg-fletchers-green-100' : 'hover:bg-fletchers-gray-95';
        return (
          <div
            key={node.id}
            className={classNames(
              'absolute right-0 left-0 flex w-full cursor-pointer flex-row items-center justify-between gap-1 overflow-hidden pr-[12px]',
              {
                'bg-fletchers-green-50 hover:!bg-fletchers-green-75': isLightGreen && !isDarkGreen,
                'bg-fletchers-green-100 hover:!bg-fletchers-green-75': isDarkGreen,
                'bg-fletchers-gray-50 hover:bg-fletchers-gray-80': !isLightGreen && !isDarkGreen,
              },
            )}
            style={{
              height: TREE_ROW_HEIGHT,
              top,
              paddingLeft: TREE_INDENT_PX + level * TREE_INDENT_PX,
              zIndex: zIndexForType(node.data.type),
            }}
            onMouseEnter={() => setHoveredId(node.id)}
            onMouseLeave={() => setHoveredId((current) => (current === node.id ? null : current))}
            onClick={() => onActivate(node)}
            onContextMenu={(e) => {
              if (!showKebab) return;
              e.preventDefault();
              onOpenContextMenu(e, node);
            }}
          >
            <SectionsTreeNodeContent data={node.data} />
            {isHovered && showKebab && (
              <button
                type="button"
                aria-label="Section options"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenContextMenu(e, node);
                }}
                className={classNames(
                  'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[4px] focus:outline-none',
                  buttonHover,
                )}
              >
                <KebabMenuIcon className="text-black" />
              </button>
            )}
            <div
              className={classNames(
                'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[4px]',
                buttonHover,
              )}
              onClick={(e) => {
                e.stopPropagation();
                node.close();
                onAfterToggle?.();
              }}
            >
              <ChevronIcon className="translate-y-px rotate-180 [&>path]:stroke-black" width={11} height={7} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
